import classNames from 'classnames';
import { format } from 'date-fns';
import { ReactNode, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { Heading, List } from 'yoso';
import { Badge } from '~/components/Badge/Badge';
import {
  ChallengeEventParticipateStatus,
  useChallengeEvents,
} from '~/components/Challenge/Event/ChallengeEvent.hooks';
import { Celect, CelectProps } from '~/components/Form/Celect';
import { T } from '~/components/I18n';
import { useMember } from '~/components/Member/Member.hooks';
import styles from './ChallengeEventCelect.module.scss';

export type ChallengeEventCelectProps = Partial<
  Pick<CelectProps, 'value' | 'className' | 'onChange'>
>;

const participateStatusXltMap: Record<ChallengeEventParticipateStatus, ReactNode> = {
  NOT_PARTICIPATED: <T name="citizen_challenge_ranking_round_popup_status1" />,
  PARTICIPATING: <T name="citizen_challenge_ranking_round_popup_status2" />,
  PARTICIPATED: <T name="citizen_challenge_ranking_round_popup_status3" />,
};

export const ChallengeEventCelect = ({ className, value, onChange }: ChallengeEventCelectProps) => {
  const {
    data: { isLoggedIn },
  } = useMember();
  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = useChallengeEvents();
  const { ref } = useInView({ onChange: (inView) => inView && fetchNextPage() });
  const items = useMemo(() => data?.pages.flatMap((page) => page.weeks) ?? [], [data]);
  return (
    <Celect.Root className={classNames(styles.root, className)} value={value} onChange={onChange}>
      <Celect.Button
        variant="text"
        className={styles.button}
        data-event="click"
        data-event-target="challengeround_btn_in_leaderboard">
        <T name="citizen_challenge_ranking_title" options={[value === '0' ? '-' : value]} />
      </Celect.Button>
      <Celect.Modal className={styles.modal}>
        <Heading level={5} className={styles.heading}>
          <T name="citizen_challenge_ranking_round_popup_title" />
        </Heading>
        <List className={styles.list}>
          {items.map(({ week, startAt, endAt, weekStatus, participateStatus }) => (
            <li key={week}>
              <Celect.Option
                value={week.toString()}
                className={classNames(styles.option, styles[participateStatus])}
                data-event="click"
                data-event-target="round_in_selectround_bottomsheet">
                <Heading level={6} className={styles.week}>
                  <T name="citizen_challenge_ranking_round_popup_list" options={[week]} />{' '}
                  {weekStatus === 'IN_PROGRESS' && (
                    <Badge color="red" variant="pill" className={styles.badge}>
                      Now
                    </Badge>
                  )}
                </Heading>
                <p className={styles.description}>
                  {format(startAt, 'PP')} ~ {format(endAt, 'PP')}
                  {isLoggedIn && (
                    <span className={styles.status}>
                      {participateStatusXltMap[participateStatus]}
                    </span>
                  )}
                </p>
              </Celect.Option>
            </li>
          ))}
          {hasNextPage && !isFetchingNextPage && (
            <li>
              <div ref={ref} className={styles.placeholder}>
                More
              </div>
            </li>
          )}
        </List>
      </Celect.Modal>
    </Celect.Root>
  );
};
